import jwtDecode from "jwt-decode";

export interface TokenDataDecoded {
    company_categories: any;
    company_id: string;
    company_id_facturation: string;
    user_id: string;
    username: string;
    roles: any;
}

export const getTokenData = (token: string): TokenDataDecoded | null => {
    return jwtDecode<TokenDataDecoded>(token);
};

export const getRolesFromTokenData = (tokenData: TokenDataDecoded | null | undefined): string[] => {
    if (!tokenData?.roles) {
        return [];
    }

    return Object.values(tokenData.roles).filter((role): role is string => typeof role === 'string');
};

// Token Manager Singleton
class TokenManager {
    private static instance: TokenManager;
    private token: string | null = null;
    private ssToken: string | null = null;
    private tokenVariable: string | null = null;
    private refreshTokenVariable: string | null = null;

    private constructor() {
        this.tokenVariable = process.env.REACT_APP_LOCALSTORAGE_TOKEN_VARIABLE || null;
        this.refreshTokenVariable = process.env.REACT_APP_LOCALSTORAGE_REFRESH_TOKEN_VARIABLE || null;
        this.loadTokensFromStorage();
    }

    public static getInstance(): TokenManager {
        if (!TokenManager.instance) {
            TokenManager.instance = new TokenManager();
        }
        return TokenManager.instance;
    }

    private loadTokensFromStorage(): void {
        if (!this.tokenVariable) {
            console.warn('REACT_APP_LOCALSTORAGE_TOKEN_VARIABLE is not defined');
            return;
        }

        const rawToken = localStorage.getItem(this.tokenVariable);
        this.token = rawToken ? this.cleanToken(rawToken) : null;
        
        const rawSSToken = localStorage.getItem(`${this.tokenVariable}_ss`);
        this.ssToken = rawSSToken ? this.cleanToken(rawSSToken) : null;
    }

    private cleanToken(token: string): string {
        return token
    }

    public getToken(): string | null {
        return this.token ? this.cleanToken(this.token) : null;
    }

    public async getSSToken(): Promise<string | null> {
        return this.getSSTokenSync();
    }

    /**
     * Synchronous version that only uses cleanToken (for cases where clipboard is not available)
     */
    public getSSTokenSync(): string | null {
        if (this.ssToken) {
            return this.cleanToken(this.ssToken);
        }
        // Fall back to the main API token so SS requests (e.g. source_boards)
        // still send Authorization after password login / session restore.
        return this.token ? this.cleanToken(this.token) : null;
    }

    /**
     * Debug method to simulate clipboard copy/read to detect invisible characters
     * This helps identify if there are hidden characters in the token
     */
    public async debugTokenViaClipboard(): Promise<{
        original: string | null;
        clipboard: string | null;
        hasInvisibleChars: boolean;
        invisibleChars: string[];
        cleaned: string | null;
    }> {
        const original = this.ssToken;
        
        if (!original) {
            return {
                original: null,
                clipboard: null,
                hasInvisibleChars: false,
                invisibleChars: [],
                cleaned: null
            };
        }

        try {
            // Copy token to clipboard
            await navigator.clipboard.writeText(original);
            
            // Read back from clipboard
            const clipboard = await navigator.clipboard.readText();
            
            // Detect invisible characters
            const invisibleChars: string[] = [];
            for (let i = 0; i < original.length; i++) {
                const char = original[i];
                const charCode = char.charCodeAt(0);
                
                // Check for invisible/control characters
                if (charCode < 32 || charCode === 127 || charCode > 159) {
                    invisibleChars.push(`'${char}' (code: ${charCode})`);
                }
            }
            
            const hasInvisibleChars = invisibleChars.length > 0;
            const cleaned = this.cleanToken(original);
            
            console.group('🔍 Token Debug - Clipboard Simulation');
            console.log('Original token length:', original.length);
            console.log('Clipboard token length:', clipboard.length);
            console.log('Are they equal?', original === clipboard);
            console.log('Has invisible chars?', hasInvisibleChars);
            if (hasInvisibleChars) {
                console.log('Invisible characters found:', invisibleChars);
            }
            console.log('Cleaned token length:', cleaned.length);
            console.log('Original vs Cleaned equal?', original === cleaned);
            console.groupEnd();
            
            return {
                original,
                clipboard,
                hasInvisibleChars,
                invisibleChars,
                cleaned
            };
        } catch (error) {
            console.error('Clipboard debug failed:', error);
            return {
                original,
                clipboard: null,
                hasInvisibleChars: false,
                invisibleChars: [],
                cleaned: this.cleanToken(original)
            };
        }
    }

    public setToken(token: string): void {
        if (!this.tokenVariable) {
            console.warn('REACT_APP_LOCALSTORAGE_TOKEN_VARIABLE is not defined');
            return;
        }

        const cleanedToken = this.cleanToken(token);
        this.token = cleanedToken;
        localStorage.setItem(this.tokenVariable, cleanedToken);
    }

    public setSSToken(token: string): void {
        if (!this.tokenVariable) {
            console.warn('REACT_APP_LOCALSTORAGE_TOKEN_VARIABLE is not defined');
            return;
        }

        const cleanedToken = this.cleanToken(token);
        this.ssToken = cleanedToken;
        localStorage.setItem(`${this.tokenVariable}_ss`, cleanedToken);
    }

    public setTokens(token: string, ssToken?: string): void {
        this.setToken(token);
        if (ssToken) {
            this.setSSToken(ssToken);
        } else {
            this.setSSToken(token); // Use same token for SS if not provided
        }
    }

    public removeToken(): void {
        if (!this.tokenVariable) {
            console.warn('REACT_APP_LOCALSTORAGE_TOKEN_VARIABLE is not defined');
            return;
        }

        this.token = null;
        this.ssToken = null;
        localStorage.removeItem(this.tokenVariable);
        localStorage.removeItem(`${this.tokenVariable}_ss`);
    }

    public setRefreshToken(refreshToken: string): void {
        if (!this.refreshTokenVariable) {
            console.warn('REACT_APP_LOCALSTORAGE_REFRESH_TOKEN_VARIABLE is not defined');
            return;
        }

        const cleanedRefreshToken = this.cleanToken(refreshToken);
        localStorage.setItem(this.refreshTokenVariable, cleanedRefreshToken);
    }

    public getRefreshToken(): string | null {
        if (!this.refreshTokenVariable) {
            console.warn('REACT_APP_LOCALSTORAGE_REFRESH_TOKEN_VARIABLE is not defined');
            return null;
        }

        const rawRefreshToken = localStorage.getItem(this.refreshTokenVariable);
        return rawRefreshToken ? this.cleanToken(rawRefreshToken) : null;
    }

    public removeRefreshToken(): void {
        if (!this.refreshTokenVariable) {
            console.warn('REACT_APP_LOCALSTORAGE_REFRESH_TOKEN_VARIABLE is not defined');
            return;
        }

        localStorage.removeItem(this.refreshTokenVariable);
    }

    public clearAll(): void {
        this.removeToken();
        this.removeRefreshToken();
    }
}

// Export singleton instance
export const tokenManager = TokenManager.getInstance();

// Legacy functions for backward compatibility
export const getToken = (): string | null => {
    return tokenManager.getToken();
};

export const getSSToken = (): Promise<string | null> => {
    return tokenManager.getSSToken();
};

export const getSSTokenSync = (): string | null => {
    return tokenManager.getSSTokenSync();
};

export const setToken = (token: string): void => {
    tokenManager.setToken(token);
};

export const setSSToken = (token: string): void => {
    tokenManager.setSSToken(token);
};

export const removeToken = (): void => {
    tokenManager.removeToken();
};

export const setTokens = (token: string, ssToken?: string): void => {
    tokenManager.setTokens(token, ssToken);
};

export const setRefreshToken = (refreshToken: string): void => {
    tokenManager.setRefreshToken(refreshToken);
};

export const getRefreshToken = (): string | null => {
    return tokenManager.getRefreshToken();
};

export const clearAllTokens = (): void => {
    tokenManager.clearAll();
};

export const debugTokenViaClipboard = (): Promise<{
    original: string | null;
    clipboard: string | null;
    hasInvisibleChars: boolean;
    invisibleChars: string[];
    cleaned: string | null;
}> => {
    return tokenManager.debugTokenViaClipboard();
};